import type { FieldGroup } from '@/components/ui/field-group-accordion';

export type DispositionFieldStatus = 'filled' | 'missing';

export interface SectionScore {
  filled: number;
  total: number;
}

/**
 * Builds a Map of field ID → disposition status ('filled' or 'missing').
 * Only fields present in requiredFields are included.
 */
export function buildFieldDispositionMap(
  requiredFields?: string[],
  missingFields?: string[],
): Map<string, DispositionFieldStatus> {
  const map = new Map<string, DispositionFieldStatus>();
  if (!requiredFields || requiredFields.length === 0) return map;

  const missingSet = new Set(missingFields ?? []);
  for (const fieldId of requiredFields) {
    map.set(fieldId, missingSet.has(fieldId) ? 'missing' : 'filled');
  }
  return map;
}

/**
 * Sorts fields so disposition-required fields come first:
 * missing fields at top, then filled required fields, then non-required fields.
 */
export function sortFieldsByDisposition<T extends { id: string }>(
  fields: T[],
  dispositionMap: Map<string, DispositionFieldStatus>,
): T[] {
  if (dispositionMap.size === 0) return fields;

  return [...fields].sort((a, b) => {
    const aStatus = dispositionMap.get(a.id);
    const bStatus = dispositionMap.get(b.id);
    const aOrder = aStatus === 'missing' ? 0 : aStatus === 'filled' ? 1 : 2;
    const bOrder = bStatus === 'missing' ? 0 : bStatus === 'filled' ? 1 : 2;
    return aOrder - bOrder;
  });
}

/**
 * Counts how many required disposition paths target the given field ID, treating
 * the field as either a scalar (exact match) or an array/object container
 * (any path starting with `${fieldId}[` or `${fieldId}.`).
 *
 * Returns undefined when no required paths match (so callers can skip rendering).
 */
export function computeFieldScore(
  fieldId: string,
  requiredFields?: string[],
  missingFields?: string[],
): SectionScore | undefined {
  if (!requiredFields || requiredFields.length === 0) return undefined;
  const missingSet = new Set(missingFields ?? []);
  const arrayPrefix = `${fieldId}[`;
  const objectPrefix = `${fieldId}.`;
  let total = 0;
  let missing = 0;
  for (const path of requiredFields) {
    if (path === fieldId || path.startsWith(arrayPrefix) || path.startsWith(objectPrefix)) {
      total++;
      if (missingSet.has(path)) missing++;
    }
  }
  if (total === 0) return undefined;
  return { filled: total - missing, total };
}

/**
 * Computes per-section completeness scores based on disposition required/missing fields.
 *
 * For each group, sums:
 *   - scalar fields whose id appears verbatim in requiredFields
 *   - array/object fields whose id is the prefix of any path in requiredFields
 *     (e.g. `drug` rolls up `drug[0].coverage.coinsurance`)
 *
 * Returns a Map keyed by group label.
 */
export function computeSectionScores(
  groups: FieldGroup[],
  requiredFields?: string[],
  missingFields?: string[],
): Map<string, SectionScore> {
  const scores = new Map<string, SectionScore>();

  if (!requiredFields || requiredFields.length === 0) {
    return scores;
  }

  for (const group of groups) {
    let total = 0;
    let missing = 0;
    for (const field of group.fields) {
      const fieldScore = computeFieldScore(field.id, requiredFields, missingFields);
      if (fieldScore) {
        total += fieldScore.total;
        missing += fieldScore.total - fieldScore.filled;
      }
    }
    if (total > 0) {
      scores.set(group.label, { filled: total - missing, total });
    }
  }

  return scores;
}

/**
 * Parses a disposition path that targets an array item.
 *
 * Examples:
 *   "drug[0].coverage.coinsurance"  → { array: "drug", index: 0, rest: "coverage.coinsurance" }
 *   "drug[12].pbm"                  → { array: "drug", index: 12, rest: "pbm" }
 *   "referral_required"             → null
 *   "drug[0]"                       → null  (no rest)
 *   "drug[abc].x"                   → null  (invalid index)
 */
export function parseArrayItemPath(
  path: string,
): { array: string; index: number; rest: string } | null {
  const match = /^([a-zA-Z_][a-zA-Z0-9_]*)\[(\d+)\]\.(.+)$/.exec(path);
  if (!match) return null;
  return {
    array: match[1],
    index: Number(match[2]),
    rest: match[3],
  };
}

/**
 * Filters required/missing fields down to a single array item, returning
 * paths *relative to the item* (the `${array}[${index}].` prefix is stripped).
 *
 * Example: extractItemDispositionFields("drug", 0, ["drug[0].coverage.coinsurance", "referral_required"], ["drug[0].coverage.coinsurance"])
 *   → { required: ["coverage.coinsurance"], missing: ["coverage.coinsurance"] }
 */
export function extractItemDispositionFields(
  arrayName: string,
  index: number,
  requiredFields?: string[],
  missingFields?: string[],
): { required: string[]; missing: string[] } {
  const filterAndStrip = (paths: string[] | undefined): string[] => {
    if (!paths) return [];
    const out: string[] = [];
    for (const path of paths) {
      const parsed = parseArrayItemPath(path);
      if (parsed && parsed.array === arrayName && parsed.index === index) {
        out.push(parsed.rest);
      }
    }
    return out;
  };

  return {
    required: filterAndStrip(requiredFields),
    missing: filterAndStrip(missingFields),
  };
}

/**
 * Computes per-subsection completeness scores for an item-relative set of
 * required/missing paths.
 *
 * Subsection prefixes are derived automatically from the first path segment
 * of each `required` path (e.g. "coverage.coinsurance" → prefix "coverage").
 * Paths without a `.` are top-level fields and produce no subsection score.
 *
 * Returns a Map keyed by subsection prefix. Sections with zero required fields
 * are omitted (so the badge component renders nothing for them).
 */
export function computeSubsectionScores(
  required: string[],
  missing: string[],
): Map<string, SectionScore> {
  const scores = new Map<string, SectionScore>();
  if (required.length === 0) return scores;

  // Derive unique subsection prefixes from the required paths
  const prefixes = new Set<string>();
  for (const path of required) {
    const dotIndex = path.indexOf('.');
    if (dotIndex > 0) {
      prefixes.add(path.substring(0, dotIndex));
    }
  }

  const missingSet = new Set(missing);

  for (const prefix of prefixes) {
    const dotPrefix = `${prefix}.`;
    let total = 0;
    let missingCount = 0;
    for (const path of required) {
      if (path.startsWith(dotPrefix)) {
        total++;
        if (missingSet.has(path)) {
          missingCount++;
        }
      }
    }
    if (total > 0) {
      scores.set(prefix, { filled: total - missingCount, total });
    }
  }

  return scores;
}
